import { DecimalPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal,
} from '@angular/core';

@Component({
  selector: 'app-upload-card',
  imports: [DecimalPipe],
  templateUrl: './upload-card.html',
  styleUrl: './upload-card.scss',
})
export class UploadCard implements OnChanges, OnDestroy {
  @Input() acceptExtensions: string[] = ['.jpg', '.jpeg', '.png', '.pdf'];
  @Input() maxSizeBytes = 3 * 1024 * 1024;
  @Input() title = 'Upload File';
  @Input() label = '';
  @Input({ alias: 'secondaryLabel' }) secondaryLabel: string = '';
  @Input() allowMultiple = false;
  @Output() fileChange = new EventEmitter<File | File[] | null>();
  @Input() url: string | string[] = '';

  files: File[] = [];
  urlList: string[] = [];
  fileName = '';
  fileSize = 0;
  error = '';
  previewUrl: string | null = null;
  isPdf = false;
  isDragging = signal(false);
  hasFile = signal(false);

  get acceptAttr() {
    return this.acceptExtensions.join(',');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && this.url) {
      if (Array.isArray(this.url)) {
        this.urlList = this.url;
        this.hasFile.set(this.url.length > 0);
      } else if (typeof this.url === 'string' && this.url) {
        const ext = this.getExtension(this.url);
        this.hasFile.set(true);
        this.isPdf = ext === 'pdf';
        if (!this.isPdf) {
          this.previewUrl = this.url;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.revokePreview();
  }

  private getExtension(url: string) {
    if (url && typeof url === 'string') {
      const m = url.split('?')[0].split('/').pop();
      return (m || '').split('.').pop()?.toLowerCase() || '';
    }
    return '';
  }

  private revokePreview() {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }

    this.previewUrl = null;
  }

  onSelect(file: File | null, fileList?: FileList | null) {
    this.error = '';
    this.fileName = '';
    this.fileSize = 0;
    this.isPdf = false;
    this.revokePreview();
    this.hasFile.set(false);

    if (this.allowMultiple && fileList) {
      const validFiles: File[] = [];
      for (let i = 0; i < fileList.length; i++) {
        const f = fileList[i];
        if (this.validateFile(f)) {
          validFiles.push(f);
        }
      }
      if (validFiles.length > 0) {
        this.files = validFiles;
        this.hasFile.set(true);
        this.fileChange.emit(validFiles);
      } else {
        this.fileChange.emit(null);
      }
      return;
    }

    if (!file) {
      this.files = [];
      this.fileChange.emit(null);
      return;
    }

    if (!this.validateFile(file)) {
      this.fileChange.emit(null);
      return;
    }

    const mime = file.type?.toLowerCase() ?? '';
    this.fileName = file.name;
    this.fileSize = file.size;
    this.files = [file];
    this.hasFile.set(true);

    if (mime === 'application/pdf' || file.name.endsWith('.pdf')) {
      this.isPdf = true;
    } else if (mime.startsWith('image/')) {
      this.previewUrl = URL.createObjectURL(file);
    }

    this.fileChange.emit(file);
  }

  private validateFile(file: File): boolean {
    if (file.size > this.maxSizeBytes) {
      this.error = `Maximum size exceeded`;
      return false;
    }

    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!this.acceptExtensions.includes(ext)) {
      this.error = `Unsupported format`;
      return false;
    }

    return true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    const files = event.dataTransfer?.files;
    if (this.allowMultiple && files) {
      this.onSelect(null, files);
    } else {
      this.onSelect(files?.[0] ?? null);
    }
  }

  clearFile(event: Event, index?: number) {
    event.preventDefault();
    event.stopPropagation();
    this.urlList = [];
    this.url = '';
    this.previewUrl = '';
    this.onSelect(null);
    // if (this.allowMultiple && index !== undefined) {
    //   this.files.splice(index, 1);
    //   if (this.files.length === 0) {
    //     this.hasFile.set(false);
    //     this.fileChange.emit(null);
    //   } else {
    //     this.fileChange.emit(this.files);
    //   }
    // } else {
    //   this.files = [];
    //   this.onSelect(null);
    // }
  }

  get supportedText() {
    const exts = this.acceptExtensions
      .map((ext) => ext.substring(1))
      .join(', ')
      .toString();

    return `Supported format: ${exts}`;
  }
}
